import axios from "axios";
import { SlaMetric } from "./generated/enums";
import type { PriorityId } from "./tickets";
import type { SupportCalendarId } from "./supportCalendars";
import type { PriorityConfigurationId } from "./projectConfigs/priorities";

export type SlaPolicyId = string & { __brand: "SlaPolicyId" };

/** A missing target means no promise for that milestone at that priority. */
export type SlaPolicyTarget = {
    priorityId: PriorityId;
    metric: SlaMetric;
    minutes: number;
};

export type SlaPolicy = {
    id: SlaPolicyId;
    name: string;
    priorityConfigurationId: PriorityConfigurationId;
    priorityConfigurationName: string;
    /** Null measures against the wall clock. */
    supportCalendarId: SupportCalendarId | null;
    supportCalendarName: string | null;
    /** Names of the projects using it. */
    projects: string[];
    targets: SlaPolicyTarget[];
};

export type SlaPolicyInput = {
    name: string;
    supportCalendarId: SupportCalendarId | null;
    targets: SlaPolicyTarget[];
};

export interface SlaPoliciesApi {
    get(): Promise<SlaPolicy[]>;
    create(
        input: SlaPolicyInput & { priorityConfigurationId: PriorityConfigurationId }
    ): Promise<SlaPolicy>;
    update(id: SlaPolicyId, input: SlaPolicyInput): Promise<SlaPolicy>;
    remove(id: SlaPolicyId): Promise<void>;
}

export const slaPoliciesApi: SlaPoliciesApi = {
    async get() {
        const { data } = await axios.get<SlaPolicy[]>("/api/v0/sla-policies");
        return data;
    },
    async create(input) {
        const { data } = await axios.post<SlaPolicy>("/api/v0/sla-policies", input);
        return data;
    },
    async update(id, input) {
        const { data } = await axios.put<SlaPolicy>(`/api/v0/sla-policies/${id}`, input);
        return data;
    },
    async remove(id) {
        await axios.delete(`/api/v0/sla-policies/${id}`);
    },
};
